import { FC, ComponentProps, useEffect, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';

import { SideBarProps, SideBarContentTitleProps, SideBarLinkProps } from './type';
import Button from '../Button';
import Icon from '../Icon';
import ProfilePhoto from '../ProfilePhoto';
import Typography from '../Typography';
import { getUniqueKey } from '../utils';
import {
  SideBarWrapperStyled,
  SideBarStyled,
  SideBarTitleStyled,
  SideBarTypography,
  SideBarDividerStyled,
  SideBarCloseButtonStyled,
  SideBarBottomContentStyled,
  SideBarTopContentStyled,
  SideBarSubContentWrapperStyled,
} from './styled';

const SideBarTitleItem = ({ ...props }: SideBarContentTitleProps) => {
  const { title, icon, clicked, level, sections, last: lastChild, openSide, variant } = props;
  const [clickedItem, setClickedItem] = useState(clicked);

  return (
    <div style={{ position: 'relative' }}>
      <SideBarTitleStyled
        openSide={openSide}
        last={lastChild}
        level={level}
        clicked={clickedItem}
        onClick={() => setClickedItem(!clickedItem)}
      >
        {level === 0 && (
          <Icon
            name={icon}
            color={clickedItem ? '#0057b8' : '#8E90A4'}
            style={{
              transition: 'all 0.3s ease-in-out',
            }}
          />
        )}

        <SideBarTypography
          className={openSide || variant === 'mobile' ? '' : `hidden`}
          variant={variant}
          level={level}
          clicked={clickedItem}
          openSide={openSide}
        >
          {title}
        </SideBarTypography>
        <Icon
          name="keyboardArrowDownRounded"
          color={clickedItem ? '#0057b8' : '#8E90A4'}
          size={openSide ? '24px' : '16px'}
          style={{
            display: openSide || variant === 'mobile' || level > 0 ? 'flex' : 'none',
            transform: `rotate(${clickedItem ? '0deg' : '-90deg'})`,
            transition: 'all 0.3s ease-in-out',
          }}
        />
      </SideBarTitleStyled>
      <SideBarSubContentWrapperStyled level={level} clicked={clickedItem} openSide={openSide}>
        {sections.map(({ ...subContentProps }, index) => {
          return (
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            <SideBarContent
              key={getUniqueKey()}
              {...subContentProps}
              level={level + 1}
              last={index === sections.length - 1}
              openSide={openSide}
              variant={variant}
            />
          );
        })}
      </SideBarSubContentWrapperStyled>
    </div>
  );
};

const SideBarLink: FC<SideBarLinkProps> = ({ url, children, ...rest }) => {
  if (!url) return <span>{children}</span>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Component = url.startsWith('/') ? NavLink : (props: any) => <Link {...props} />;
  type InnerComponentProps = ComponentProps<typeof Component>;
  return (
    <span>
      <Component
        {...({ ...rest, to: url, style: { textDecoration: 'none' } } as InnerComponentProps)}
      >
        {children}
      </Component>
    </span>
  );
};

const SideBarContent = ({ ...props }: SideBarContentTitleProps) => {
  const { title, icon, sections, color, url, onClick, ...sideBarTitleProps } = props;
  const { openSide, last, level, variant } = sideBarTitleProps;

  if (!sections || !sections.length) {
    return (
      <SideBarLink key={getUniqueKey()} url={url} onClick={onClick}>
        <SideBarTitleStyled {...props} last={last}>
          {level === 0 && <Icon name={icon} color={color || '#8E90A4'} />}
          <SideBarTypography
            className={openSide || variant === 'mobile' ? '' : 'hidden'}
            level={level}
            openSide={openSide}
            style={color ? { color } : {}}
          >
            {title}
          </SideBarTypography>
        </SideBarTitleStyled>
      </SideBarLink>
    );
  }

  return (
    <div key={getUniqueKey()}>
      <SideBarTitleItem {...props} />
    </div>
  );
};

const SideBarBottomContent = ({ open, bottomContent: content, variant }: SideBarProps) => {
  return (
    <div
      style={{
        width: open || variant === 'mobile' ? '100%' : '100px',
        transform: 'width 0.3s ease-in-out',
      }}
    >
      <SideBarDividerStyled />
      <SideBarBottomContentStyled openSide={open}>
        {content &&
          content.map(({ ...subContentProps }) => {
            return (
              <SideBarContent
                key={getUniqueKey()}
                {...subContentProps}
                level={0}
                openSide={open}
                variant={variant}
              />
            );
          })}
      </SideBarBottomContentStyled>
    </div>
  );
};

const SideBarTopContent = ({ userPhoto, userName, open, variant, onClickPhoto }: SideBarProps) => {
  return (
    <div
      style={{
        width: open || variant === 'mobile' ? '100%' : '100px',
        transform: 'width 0.3s ease-in-out',
      }}
    >
      <SideBarTopContentStyled openSide={open} variant={variant} onClick={onClickPhoto}>
        <ProfilePhoto size={open || variant === 'mobile' ? 60 : 30} src={userPhoto} />
        <div
          style={{
            display: open || variant === 'mobile' ? 'flex' : 'none',
            flexDirection: 'column',
          }}
        >
          <Typography
            color="#0057B8"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '130px',
              fontSize: '14px',
              lineHeight: '20px',
            }}
          >
            {userName}
          </Typography>
          {onClickPhoto && (
            <Button
              variant="ghost"
              size="small"
              endAdornment={
                <Icon
                  name="keyboardArrowLeftRounded"
                  size="14px"
                  color="#0057b8"
                  style={{
                    transform: 'rotate(180deg)',
                  }}
                />
              }
              style={{
                padding: '0px 0px',
                gap: '5px',
                height: 'auto',
                lineHeight: '13px',
              }}
              contentStyle={{ fontSize: '13px' }}
            >
              Ir al perfil
            </Button>
          )}
        </div>
      </SideBarTopContentStyled>
      <SideBarDividerStyled />
    </div>
  );
};

const SideBar = ({ onClickPhoto, ...props }: SideBarProps) => {
  const { sections, open, onClose, onOpen, variant, wrapperComponent } = props;
  const [openSide, setOpenSide] = useState(open);

  useEffect(() => {
    setOpenSide(open);
    if (open && onOpen) {
      onOpen();
    } else if (!open && onClose) {
      onClose();
    }
  }, [open, onOpen, onClose]);

  return (
    <SideBarWrapperStyled data-testid="sideBar-testId-wrapper" {...props} open={openSide}>
      <SideBarCloseButtonStyled variant={variant} onClick={() => setOpenSide(!openSide)}>
        <Icon
          name="keyboardArrowLeftRounded"
          color="#000"
          size="16px"
          style={{
            transform: `rotate(${openSide ? '0deg' : '-180deg'})`,
            transition: 'transform 0.3s ease-in-out',
          }}
        />
      </SideBarCloseButtonStyled>

      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'max-content 1fr max-content',
          height: '100vh',
        }}
      >
        <SideBarTopContent
          {...props}
          onClickPhoto={onClickPhoto}
          open={openSide}
          data-testid="sideBar-testId-top-content"
        />
        <div
          data-testid="sideBar-testId-middle-content"
          style={{
            overflowY: 'scroll',
            width: openSide || variant === 'mobile' ? '100%' : '105vw',
            pointerEvents: 'none',
          }}
        >
          <SideBarStyled {...props}>
            {sections.map(({ ...subContentProps }) => {
              return (
                <SideBarContent
                  key={getUniqueKey()}
                  {...subContentProps}
                  level={0}
                  openSide={openSide}
                  variant={variant}
                  wrapperComponent={wrapperComponent}
                />
              );
            })}
          </SideBarStyled>
        </div>
        <SideBarBottomContent
          {...props}
          open={openSide}
          data-testid="sideBar-testId-bottom-content"
        />
      </div>
    </SideBarWrapperStyled>
  );
};

SideBar.defaultProps = {
  photoURL: null,
  sections: [],
  bottomContent: [],
};

export default SideBar;
